import React from "react";

import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { Box, Typography } from "@mui/material";
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import { useSchedule } from "../SchedulerProvider";
import { formatMinutesRange, intervalMinutes } from "../utils/calendar";

type Props = {
  focusedDate: Date;
  onSelectDate: (date: Date) => void;
};

const WEEKDAY_LABELS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MAX_CHIPS = 3;

const CalendarMonthView: React.FC<Props> = observer(
  ({ focusedDate, onSelectDate }) => {
    const { classes, cx } = useStyles();
    const schedule = useSchedule();

    const monthStart = startOfMonth(focusedDate);
    const days = eachDayOfInterval({
      start: startOfWeek(monthStart),
      end: endOfWeek(endOfMonth(monthStart)),
    });

    return (
      <Box className={classes.root}>
        <div className={classes.weekdayRow}>
          {WEEKDAY_LABELS.map((label) => (
            <div key={label} className={classes.weekdayCell}>
              {label}
            </div>
          ))}
        </div>
        <div className={classes.grid}>
          {days.map((date) => {
            const { rule, isOverride } = schedule.effectiveRuleForDate(date);
            const intervals = rule.presentIntervals
              .map((interval) => intervalMinutes(interval))
              .filter((minutes): minutes is { start: number; end: number } =>
                Boolean(minutes)
              )
              .sort((a, b) => a.start - b.start);
            const inMonth = isSameMonth(date, monthStart);

            return (
              <button
                type="button"
                key={date.toISOString()}
                className={cx(
                  classes.cell,
                  !inMonth && classes.cellMuted,
                  isToday(date) && classes.cellToday
                )}
                onClick={() => onSelectDate(date)}
              >
                <div className={classes.cellHeader}>
                  <Typography className={classes.dayNumber}>
                    {format(date, "d")}
                  </Typography>
                  {isOverride && (
                    <EventRepeatIcon className={classes.overrideIcon} />
                  )}
                </div>
                <div className={classes.chips}>
                  {intervals.slice(0, MAX_CHIPS).map((minutes, index) => (
                    <span
                      key={index}
                      className={cx(
                        classes.chip,
                        isOverride
                          ? classes.chipOverride
                          : classes.chipRecurring
                      )}
                    >
                      {formatMinutesRange(minutes.start, minutes.end)}
                    </span>
                  ))}
                  {intervals.length > MAX_CHIPS && (
                    <span className={classes.more}>
                      +{intervals.length - MAX_CHIPS} more
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Box>
    );
  }
);

const useStyles = makeStyles()(() => ({
  root: {
    width: "100%",
    backgroundColor: "#fff",
    border: "1px solid #DEE6F0",
    borderRadius: 8,
    overflow: "hidden",
  },
  weekdayRow: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    borderBottom: "1px solid #DEE6F0",
  },
  weekdayCell: {
    padding: "8px 0",
    textAlign: "center",
    fontSize: 11,
    fontWeight: 700,
    color: "#7D8797",
    letterSpacing: 0.5,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
  },
  cell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 4,
    minHeight: 108,
    padding: 6,
    textAlign: "left",
    background: "#fff",
    border: "none",
    borderRight: "1px solid #EDF0F5",
    borderBottom: "1px solid #EDF0F5",
    cursor: "pointer",
    "&:hover": { backgroundColor: "rgba(61,109,240,0.04)" },
  },
  cellMuted: {
    backgroundColor: "#FAFBFD",
    "& $dayNumber": { color: "#C3CAD6" },
  },
  cellToday: {
    "& $dayNumber": {
      color: "#fff",
      backgroundColor: "#3D6DF0",
      borderRadius: "50%",
    },
  },
  cellHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dayNumber: {
    fontSize: 13,
    fontWeight: 700,
    color: "#363E45",
    height: 22,
    minWidth: 22,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  overrideIcon: {
    fontSize: 14,
    color: "#2E9E5B",
  },
  chips: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  chip: {
    fontSize: 10,
    fontWeight: 700,
    padding: "1px 5px",
    borderRadius: 4,
    borderStyle: "solid",
    borderWidth: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  chipRecurring: {
    backgroundColor: "#E8EEFF",
    borderColor: "#3D6DF0",
    color: "#20305C",
  },
  chipOverride: {
    backgroundColor: "#E7F6EC",
    borderColor: "#2E9E5B",
    color: "#1C5C36",
  },
  more: {
    fontSize: 10,
    fontWeight: 600,
    color: "#7D8797",
  },
}));

export default CalendarMonthView;
